import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Link from 'flarum/common/components/Link';
import humanTime from 'flarum/common/helpers/humanTime';
import { splitState } from '../state';

const SORTS = ['latest', 'oldest', 'relevant'];

/**
 * Last loaded comments, kept while the user reads a discussion, so that going
 * back restores the list (with every "more" page already opened) instead of
 * starting the search over.
 */
let remembered = null;

/** In-app route of a server-built absolute URL, so navigation does not reload the page. */
function routeOf(url) {
  const base = app.forum.attribute('baseUrl') || '';

  return url.startsWith(base) ? url.slice(base.length) || '/' : url;
}

/**
 * Individual matching comments, shown below the discussion list.
 */
export default class SplitComments extends Component {
  oninit(vnode) {
    super.oninit(vnode);

    app.splitComments = this;
    this.sort = remembered && remembered.query === this.attrs.query ? remembered.sort : 'latest';

    if (this.restore()) return;

    this.reload();
  }

  onremove(vnode) {
    super.onremove(vnode);

    if (app.splitComments === this) app.splitComments = null;
  }

  onbeforeupdate(vnode, old) {
    super.onbeforeupdate(vnode, old);

    // A new search: restart (async, never redraw while rendering).
    if (vnode.attrs.query !== this.query) setTimeout(() => this.reload());
  }

  key() {
    return [this.attrs.query, this.sort, splitState.range].join('|');
  }

  /** Reuse the remembered list when it belongs to exactly this search. */
  restore() {
    if (!remembered || remembered.key !== this.key()) return false;

    this.query = this.attrs.query;
    this.section = remembered.section;
    this.loading = false;
    this.failed = false;

    return true;
  }

  remember() {
    remembered = { key: this.key(), query: this.attrs.query, sort: this.sort, section: this.section };
  }

  fetch(offset, fuzzy) {
    const query = this.attrs.query;

    return app
      .request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/split-search/comments',
        params: { q: query, sort: this.sort, range: splitState.range, fuzzy: fuzzy ? 1 : 0, offset },
        errorHandler: (error) => {
          throw error;
        },
      })
      .then((data) => {
        if (query !== this.attrs.query) throw { stale: true };
        return data.comments;
      });
  }

  reload() {
    this.query = this.attrs.query;
    this.loading = true;
    this.failed = false;

    this.fetch(0, false).then(
      (section) => {
        this.section = section;
        this.remember();
        this.loading = false;
        m.redraw();
      },
      (error) => {
        if (error && error.stale) return;
        this.failed = true;
        this.loading = false;
        m.redraw();
      }
    );
  }

  more() {
    this.loadingMore = true;

    this.fetch(this.section.nextOffset, this.section.fuzzy).then(
      (next) => {
        this.section = { ...next, items: this.section.items.concat(next.items) };
        this.remember();
        this.loadingMore = false;
        m.redraw();
      },
      () => {
        this.loadingMore = false;
        m.redraw();
      }
    );
  }

  view() {
    return (
      <section className="SplitSearch-comments">
        <h2>
          {app.translator.trans('happydigital-split-search.forum.page.comments_heading')}
          <select
            className="FormControl SplitSearch-sort"
            value={this.sort}
            onchange={(e) => {
              this.sort = e.target.value;
              this.reload();
            }}
          >
            {SORTS.map((sort) => (
              <option value={sort}>{app.translator.trans(`happydigital-split-search.forum.sort.${sort}`)}</option>
            ))}
          </select>
        </h2>
        {this.body()}
      </section>
    );
  }

  body() {
    if (this.loading) return <LoadingIndicator />;
    if (this.failed) return <p className="SplitSearch-none">{app.translator.trans('happydigital-split-search.forum.page.error.failed')}</p>;

    const { items, nextOffset } = this.section;

    if (!items.length) return <p className="SplitSearch-none">{app.translator.trans('happydigital-split-search.forum.page.no_comments')}</p>;

    return [
      <ul className="SplitSearch-commentList">
        {items.map((comment) => (
          <li className="SplitSearch-comment">
            <Link href={routeOf(comment.url)} className="SplitSearch-commentTitle">
              {comment.discussionTitle}
            </Link>
            <Link href={routeOf(comment.url)} className="SplitSearch-excerpt">
              {m.trust(comment.excerptHtml)}
            </Link>
            <div className="SplitSearch-meta">
              {comment.author} · {humanTime(comment.createdAt)}
            </div>
          </li>
        ))}
      </ul>,
      nextOffset === null || nextOffset === undefined ? null : (
        <Button className="Button SplitSearch-more" loading={!!this.loadingMore} onclick={() => this.more()}>
          {app.translator.trans('happydigital-split-search.forum.page.more_comments')}
        </Button>
      ),
    ];
  }
}
